import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { warehouseAPI } from '../api/warehouseAPI';

const STORAGE_KEY = 'currentWarehouseId';

export const WarehouseContext = createContext({
    currentWarehouse: null,
    warehouses: [],
    loading: true,
    error: null,
    setCurrentWarehouse: () => {},
    getWarehouseAbbreviation: () => '???',
});

/**
 * Hook to access warehouse context
 */
export const useWarehouse = () => {
    const context = useContext(WarehouseContext);
    if (!context) {
        throw new Error('useWarehouse must be used within a WarehouseProvider');
    }
    return context;
};

/**
 * Get 3-letter abbreviation from warehouse name
 */
export const getWarehouseAbbreviation = (warehouseName) => {
    if (!warehouseName) return '???';
    return warehouseName.replace(/\s+/g, '').substring(0, 3).toUpperCase();
};

export const WarehouseProvider = ({ children }) => {
    const [currentWarehouse, setCurrentWarehouseState] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Load warehouses from API
     */
    const loadWarehouses = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await warehouseAPI.getAll(1, 1000, '', false);
            // Filter to only active warehouses
            const activeWarehouses = (data.results || []).filter(wh => wh.is_active);
            setWarehouses(activeWarehouses);

            // Try to restore saved warehouse from localStorage
            const savedWarehouseId = localStorage.getItem(STORAGE_KEY);
            if (savedWarehouseId) {
                const savedWarehouse = activeWarehouses.find(
                    wh => (wh._id || wh.id) === parseInt(savedWarehouseId, 10)
                );
                if (savedWarehouse) {
                    setCurrentWarehouseState(savedWarehouse);
                }
            }
        } catch (err) {
            console.error('Failed to load warehouses:', err);
            setError(err.message || 'Failed to load warehouses');
            setWarehouses([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Load warehouses on mount
     */
    useEffect(() => {
        loadWarehouses();
    }, [loadWarehouses]);

    /**
     * Set current warehouse and persist to localStorage
     */
    const setCurrentWarehouse = useCallback((warehouse) => {
        setCurrentWarehouseState(warehouse);
        if (warehouse) {
            const warehouseId = warehouse._id || warehouse.id;
            localStorage.setItem(STORAGE_KEY, warehouseId.toString());
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const value = {
        currentWarehouse,
        warehouses,
        loading,
        error,
        setCurrentWarehouse,
        getWarehouseAbbreviation,
    };

    return (
        <WarehouseContext.Provider value={value}>
            {children}
        </WarehouseContext.Provider>
    );
};
